"use client";

import { useState, useEffect } from "react";
import { link } from "src/shared/links";
import { MobileMenuButton } from "./mobile-menu";


interface NavItem {
  href: string;
  label: string;
}

const navItems: NavItem[] = [
  { href: "/parade-gallery", label: "Gallery" },
  { href: link("/sponsors"), label: "Sponsors" },
  { href: "/updates", label: "Updates" },
  { href: link("/road-closures"), label: "Road Closures" },
  { href: "/contact-us", label: "Contact" },
];

export function Navbar({ currentPath }: { currentPath?: string }) {
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkPath = () => setIsAdmin(window.location.pathname.startsWith("/admin"));
    checkPath();

    // Re-check on popstate (back/forward) and on any link navigation
    window.addEventListener("popstate", checkPath);
    const observer = new MutationObserver(checkPath);
    observer.observe(document.body, { childList: true, subtree: true });

    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("popstate", checkPath);
      observer.disconnect();
    };
  }, []);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-1000 ease-in-out px-2 sm:px-4 lg:px-8 pt-2 ${
      scrolled ? "sm:px-6 lg:px-12 sm:pt-3" : ""
    }`}>
      <nav
        className={`relative flex justify-between items-center w-full backdrop-blur-xl backdrop-saturate-150 border border-white/20 transition-all duration-1000 ease-in-out px-4 py-1.5 rounded-full mx-auto ${
          scrolled
            ? "sm:px-6 min-[812px]:py-2 shadow-[0_8px_40px_rgba(0,0,0,0.35)] max-w-5xl"
            : "sm:px-8 min-[812px]:py-4 shadow-[0_4px_20px_rgba(0,0,0,0.25)] border-transparent max-w-full"
        }`}
        style={{ background: scrolled
          ? "linear-gradient(105deg, rgba(0,40,104,0.75) 0%, rgba(0,40,104,0.75) 28%, rgba(255,255,255,0.75) 42%, rgba(255,255,255,0.75) 58%, rgba(196,30,58,0.75) 72%, rgba(196,30,58,0.75) 100%)"
          : "linear-gradient(105deg, rgba(0,40,104,0.75) 0%, rgba(0,40,104,0.75) 28%, rgba(255,255,255,0.75) 42%, rgba(255,255,255,0.75) 58%, rgba(196,30,58,0.75) 72%, rgba(196,30,58,0.75) 100%)"
        }}
      >
        {/* Skip to content */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-60 focus:bg-accent focus:text-bunting-navy focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold">
          Skip to content
        </a>

        {/* Logo / Brand */}
        <a className="flex items-center gap-2.5 transition-all duration-300" href={link("/")}>
          <img
            src="/images/favicon.svg"
            alt="Pocket Parade"
            className={`rounded transition-all duration-300 ${scrolled ? "w-12 h-7" : "w-14 h-8"}`}
          />
          <div className="flex flex-col leading-none">
            <span className={`font-display font-extrabold text-white tracking-tight transition-all duration-300 ${
              scrolled ? "text-sm min-[812px]:text-base" : "text-base min-[812px]:text-lg"
            }`}>
              Pocket Parade
            </span>
            <span className={`text-accent/70 tracking-[0.15em] uppercase font-semibold transition-all duration-300 ${
              scrolled ? "text-[8px] min-[812px]:text-[9px]" : "text-[9px] min-[812px]:text-[10px]"
            }`}>
              July 4th 2026
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <div className="hidden min-[812px]:flex whitespace-nowrap items-center gap-1 text-sm font-medium text-white" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
          {isAdmin ? (
            <>
              <a href="/api/admin/export" download className="relative px-3 py-2 rounded-md hover:text-accent hover:bg-white/5 transition-colors">Export</a>
              <a
                href="/logout"
                className={`ml-3 bg-white/15 text-white font-semibold rounded-lg hover:bg-white/25 active:scale-[0.98] transition-all ${
                  scrolled ? "text-xs px-4 py-1.5" : "text-sm px-5 py-2"
                }`}
              >
                Logout
              </a>
            </>
          ) : (
            <>
              {navItems.map((item) => {
                const isActive = currentPath === item.href;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className={`relative px-3 py-2 rounded-md transition-colors ${
                      isActive
                        ? "text-accent"
                        : "hover:text-accent hover:bg-white/5"
                    }`}
                    {...(isActive ? { "aria-current": "page" as const } : {})}
                  >
                    {item.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-accent rounded-full" />
                    )}
                  </a>
                );
              })}
              <a
                href="/registration-forms-waiver"
                className={`ml-3 bg-liberty-blue text-white font-semibold rounded-lg hover:bg-liberty-blue/90 active:scale-[0.98] transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                  scrolled ? "text-xs px-4 py-1.5" : "text-sm px-5 py-2"
                }`}
              >
                Volunteer
              </a>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex min-[812px]:hidden items-center gap-3">
          {isAdmin ? (
            <>
              <a href="/logout" className="bg-white/15 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-white/25 transition-colors">
                Logout
              </a>
            </>
          ) : (
            <>
              <a
                href="/registration-forms-waiver"
                className="bg-liberty-blue text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-liberty-blue/90 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                Volunteer
              </a>
              <MobileMenuButton>
                {navItems.map((item) => {
                  const isActive = currentPath === item.href;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      className={`py-1 transition-colors ${
                        isActive ? "text-accent font-semibold" : "hover:text-summer-sky"
                      }`}
                      {...(isActive ? { "aria-current": "page" as const } : {})}
                    >
                      {item.label}
                    </a>
                  );
                })}
                <a
                  href="/registration-forms-waiver"
                  className="bg-parade-red text-white text-sm font-semibold px-4 py-2.5 rounded-lg text-center hover:bg-parade-red/90 transition-colors mt-2"
                >
                  Volunteer
                </a>
              </MobileMenuButton>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}
